import type { QueryClient } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getSubgraphConfig, SubgraphType } from 'config/subgraph'
import { fetchBlockMap } from 'hooks/backend/blocks'
import { useConnection } from 'hooks/useConnection'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import { capBlocksToLatestIndexedBlock } from 'hooks/useSubgraphLatestIndexedBlock'
import { useEffect, useMemo, useState } from 'react'
import { TokenSnapshot, UnifiedTokenData } from 'types/analytics'
import { buildSelectionKey, dedupeIds, toNumber } from 'utils/analytics'
import { get2DayChange, getPercentChange } from 'utils/analytics/data'
import { useDeltaTimestamps } from 'utils/queries'

import type {
  GetV3TokensByIdsQuery,
  GetV3TokensByIdsQueryVariables,
  GetV3TopTokensQuery,
  GetV3TopTokensQueryVariables,
} from '../../queries/analytics/v3/generated'
import {
  useGetV3TokensByIdsQuery,
  useGetV3TokensSnapshotsQuery,
  useGetV3TopTokensQuery,
} from '../../queries/analytics/v3/generated'

type GraphV3Token = GetV3TopTokensQuery['tokens'][number] | GetV3TokensByIdsQuery['tokens'][number]

type GraphV3Bundle = GetV3TopTokensQuery['bundles'][number] | GetV3TokensByIdsQuery['bundles'][number]

const v3TokensBaseQueryKey = (chainId: number, selectionKey: string) =>
  ['analytics', 'v3Tokens', chainId, 'base', selectionKey] as const

export const v3TokensMetricsQueryKey = (
  chainId: number,
  selectionKey: string,
  t24: number,
  t48: number,
  tWeek: number,
  latestIndexedBlockNumber?: number
) =>
  [
    'analytics',
    'v3Tokens',
    chainId,
    'metrics',
    selectionKey,
    t24,
    t48,
    tWeek,
    latestIndexedBlockNumber ?? 'latest',
  ] as const

export const v3TokensQueryKey = (chainId: number, selectionKey: string, t24: number, t48: number, tWeek: number) =>
  ['analytics', 'v3Tokens', chainId, 'full', selectionKey, t24, t48, tWeek] as const

/** ─────────────────────────── Helpers ─────────────────────────── */

const parseTokenSnapshot = (tokens: GraphV3Token[], ethPriceUSD?: GraphV3Bundle['ethPriceUSD']): TokenSnapshot[] => {
  const ethPrice = ethPriceUSD ? Number(ethPriceUSD) : 0

  return tokens
    .filter((token): token is GraphV3Token => Boolean(token))
    .map((token) => {
      const derivedETH = toNumber(token.derivedETH ?? undefined)
      return {
        id: token.id,
        address: token.id,
        symbol: token.symbol ?? '',
        name: token.name ?? '',
        derivedETH,
        volumeUSD: toNumber(token.volumeUSD ?? undefined),
        volume: toNumber(token.volume ?? undefined),
        feesUSD: toNumber(token.feesUSD ?? undefined),
        txCount: toNumber(token.txCount ?? undefined),
        totalValueLocked: toNumber(token.totalValueLocked ?? undefined),
        totalValueLockedUSD: toNumber(token.totalValueLockedUSD ?? undefined),
        priceUSD: ethPrice ? derivedETH * ethPrice : 0,
      }
    })
}

const mapSnapshotsById = (tokens: TokenSnapshot[]) =>
  tokens.reduce<Record<string, TokenSnapshot>>((acc, token) => {
    acc[token.id.toLowerCase()] = token
    return acc
  }, {})

export const dedupeV3TokenIds = (ids?: string[]) => dedupeIds(ids)

export const buildV3TokenSelectionKey = (normalizedTokenIds?: string[]) => {
  return buildSelectionKey(normalizedTokenIds)
}

/** ─────────────────────────── Fetchers ─────────────────────────── */

const fetchTokensAtBlock = async (_chainId: number, endpoint: string, tokenIds?: string[], block?: number) => {
  const blockInput = block !== undefined ? { number: block } : undefined

  if (tokenIds) {
    const variables: GetV3TokensByIdsQueryVariables = {
      ids: tokenIds,
      ...(blockInput ? { block: blockInput } : {}),
    }

    const response = await useGetV3TokensByIdsQuery.fetcher({ endpoint }, variables)()

    const ethPrice = response.bundles?.[0]?.ethPriceUSD
    return parseTokenSnapshot(response.tokens ?? [], ethPrice)
  }

  const variables: GetV3TopTokensQueryVariables | undefined = blockInput ? { block: blockInput } : undefined
  const response = await useGetV3TopTokensQuery.fetcher({ endpoint }, variables)()
  const ethPrice = response.bundles?.[0]?.ethPriceUSD
  return parseTokenSnapshot(response.tokens ?? [], ethPrice)
}

const fetchCurrentV3Tokens = async (
  chainId: number,
  endpoint: string,
  normalizedTokenIds?: string[]
): Promise<TokenSnapshot[]> => {
  if (normalizedTokenIds && normalizedTokenIds.length === 0) return []

  return fetchTokensAtBlock(chainId, endpoint, normalizedTokenIds)
}

interface V3TokenMetrics
  extends Pick<
    UnifiedTokenData,
    | 'volumeUSD'
    | 'volumeUSDChange'
    | 'volumeUSDWeek'
    | 'priceUSDChange'
    | 'priceUSDChangeWeek'
    | 'fees24hUSD'
    | 'tvl24hUSDChange'
  > {
  metricsLoaded: boolean
  metrics24hLoaded: boolean
  metrics7dLoaded: boolean
}

const computeV3TokenMetrics = (
  current: TokenSnapshot | undefined,
  oneDay: TokenSnapshot | undefined,
  twoDays: TokenSnapshot | undefined,
  week: TokenSnapshot | undefined,
  resolved: { day: boolean; week: boolean },
  previous?: V3TokenMetrics
): V3TokenMetrics => {
  const hasCurrent = Boolean(current)
  const metrics24hLoaded = resolved.day && hasCurrent
  const hasWeeklySnapshot = resolved.week && hasCurrent && Boolean(week)
  const metrics7dLoaded = (resolved.week && hasCurrent) || previous?.metrics7dLoaded || false

  if (!current) {
    return {
      volumeUSD: 0,
      volumeUSDChange: 0,
      volumeUSDWeek: previous?.volumeUSDWeek ?? 0,
      priceUSDChange: 0,
      priceUSDChangeWeek: previous?.priceUSDChangeWeek ?? 0,
      fees24hUSD: 0,
      tvl24hUSDChange: 0,
      metricsLoaded: metrics24hLoaded && metrics7dLoaded,
      metrics24hLoaded,
      metrics7dLoaded,
    }
  }

  const [computedVolume24h, computedVolumeChange] =
    current && oneDay && twoDays
      ? get2DayChange(current.volumeUSD.toString(), oneDay.volumeUSD.toString(), twoDays.volumeUSD.toString())
      : [oneDay ? Number(current.volumeUSD) - Number(oneDay.volumeUSD) : Number(current.volumeUSD), 0]

  const volume7d = week ? Number(current.volumeUSD) - Number(week.volumeUSD) : Number(current.volumeUSD)

  const fees24h = oneDay ? Number(current.feesUSD) - Number(oneDay.feesUSD) : Number(current.feesUSD)

  const normalizedVolumeUSD = Number.isFinite(computedVolume24h) ? Math.max(computedVolume24h, 0) : 0
  const normalizedVolumeUSDWeek = hasWeeklySnapshot
    ? Number.isFinite(volume7d)
      ? Math.max(volume7d, 0)
      : 0
    : previous?.volumeUSDWeek ?? (Number.isFinite(volume7d) ? Math.max(volume7d, 0) : 0)

  const tvl24hChange = getPercentChange(current.totalValueLockedUSD.toString(), oneDay?.totalValueLockedUSD.toString())

  const priceUSDChange = getPercentChange(current.priceUSD?.toString(), oneDay?.priceUSD?.toString())

  const priceUSDChangeWeek = hasWeeklySnapshot
    ? getPercentChange(current.priceUSD?.toString(), week?.priceUSD?.toString())
    : previous?.priceUSDChangeWeek ?? getPercentChange(current.priceUSD?.toString(), week?.priceUSD?.toString())

  return {
    volumeUSD: normalizedVolumeUSD,
    volumeUSDChange: computedVolumeChange,
    volumeUSDWeek: normalizedVolumeUSDWeek,
    priceUSDChange,
    priceUSDChangeWeek,
    fees24hUSD: fees24h,
    tvl24hUSDChange: tvl24hChange,
    metricsLoaded: metrics24hLoaded && metrics7dLoaded,
    metrics24hLoaded,
    metrics7dLoaded,
  }
}

type FetchV3TokenMetricsOptions = {
  providedBlocks?: Map<number, number>
  previousMetrics?: Record<string, V3TokenMetrics>
  latestIndexedBlockNumber?: number
}

type SharedMetricsOptions = {
  blocks?: Map<number, number>
  timestamps?: [number, number, number]
  blocksRequired?: boolean
  latestIndexedBlockNumber?: number
}

const fetchV3TokenMetrics = async (
  chainId: number,
  endpoint: string,
  currentTokens: TokenSnapshot[],
  normalizedTokenIds: string[] | undefined,
  t24: number,
  t48: number,
  tWeek: number,
  options?: FetchV3TokenMetricsOptions
): Promise<Record<string, V3TokenMetrics>> => {
  if (!currentTokens.length) return {}

  const { providedBlocks, previousMetrics, latestIndexedBlockNumber } = options ?? {}

  const tokenIdsToProcess =
    normalizedTokenIds && normalizedTokenIds.length > 0
      ? normalizedTokenIds
      : currentTokens.map((token) => token.id.toLowerCase())

  const timestamps = [t24, t48, tWeek]
  const uncappedBlocks = providedBlocks ?? (await fetchBlockMap({ chainId, timestamps }))
  const blocks = capBlocksToLatestIndexedBlock(uncappedBlocks, latestIndexedBlockNumber)

  const block24 = blocks.get(t24)
  const block48 = blocks.get(t48)
  const blockWeek = blocks.get(tWeek)

  let oneDaySnapshots: TokenSnapshot[] = []
  let twoDaySnapshots: TokenSnapshot[] = []
  let weekSnapshots: TokenSnapshot[] | undefined

  if (tokenIdsToProcess.length > 0 && (block24 !== undefined || block48 !== undefined || blockWeek !== undefined)) {
    const variables = {
      ids: tokenIdsToProcess,
      block24: block24 ? { number: block24 } : undefined,
      block48: block48 ? { number: block48 } : undefined,
      blockWeek: blockWeek ? { number: blockWeek } : undefined,
    }

    const snapshots = await useGetV3TokensSnapshotsQuery.fetcher({ endpoint }, variables)()

    if (block24 !== undefined) {
      const bundlePrice = snapshots.block24Bundle?.[0]?.ethPriceUSD
      oneDaySnapshots = parseTokenSnapshot(snapshots.block24 ?? [], bundlePrice)
    }

    if (block48 !== undefined) {
      const bundlePrice = snapshots.block48Bundle?.[0]?.ethPriceUSD
      twoDaySnapshots = parseTokenSnapshot(snapshots.block48 ?? [], bundlePrice)
    }

    if (blockWeek !== undefined) {
      const bundlePrice = snapshots.blockWeekBundle?.[0]?.ethPriceUSD
      weekSnapshots = parseTokenSnapshot(snapshots.blockWeek ?? [], bundlePrice)
    }
  }

  const currentById = mapSnapshotsById(currentTokens)
  const oneDayById = mapSnapshotsById(oneDaySnapshots)
  const twoDayById = mapSnapshotsById(twoDaySnapshots)
  const dayResolved = true

  const computeMetrics = (
    weekById?: Record<string, TokenSnapshot>,
    weekResolved = false,
    previousOverride?: Record<string, V3TokenMetrics>
  ) =>
    currentTokens.reduce<Record<string, V3TokenMetrics>>((acc, token) => {
      const id = token.id.toLowerCase()
      const previous = previousOverride?.[id] ?? previousMetrics?.[id]
      const metrics = computeV3TokenMetrics(
        currentById[id],
        oneDayById[id],
        twoDayById[id],
        weekById?.[id],
        {
          day: dayResolved,
          week: weekResolved,
        },
        previous
      )
      acc[id] = metrics
      return acc
    }, {})

  const weekById = weekSnapshots ? mapSnapshotsById(weekSnapshots) : undefined
  const weekResolved = Boolean(blockWeek && weekSnapshots)
  const metrics = computeMetrics(weekById, weekResolved, previousMetrics)

  return metrics
}

const createV3TokenData = (token: TokenSnapshot, overrides?: Partial<UnifiedTokenData>): UnifiedTokenData => {
  const base: UnifiedTokenData = {
    id: token.id,
    address: token.address,
    name: token.name,
    symbol: token.symbol,
    derivedETH: token.derivedETH,
    priceUSD: token.priceUSD,
    priceUSDChange: 0,
    priceUSDChangeWeek: 0,
    volumeUSD: 0,
    volumeUSDChange: 0,
    volumeUSDWeek: 0,
    tvlToken: token.totalValueLocked,
    tvlUSD: token.totalValueLockedUSD,
    metricsLoaded: false,
    metrics24hLoaded: false,
    metrics7dLoaded: false,
    fees24hUSD: 0,
    tvl24hUSDChange: 0,
  }

  return overrides ? { ...base, ...overrides } : base
}

export const prefetchV3TopTokens = async (
  queryClient: QueryClient,
  chainId: number,
  t24: number,
  t48: number,
  tWeek: number,
  blocks?: Map<number, number>,
  latestIndexedBlockNumber?: number
) => {
  let endpoint: string
  try {
    endpoint = getSubgraphConfig(chainId, SubgraphType.V3).endpoint
  } catch (error) {
    console.error(error)
    return
  }

  const selectionKey = buildV3TokenSelectionKey(undefined)

  const baseTokens =
    (await queryClient.ensureQueryData({
      queryKey: v3TokensBaseQueryKey(chainId, selectionKey),
      queryFn: () => fetchCurrentV3Tokens(chainId, endpoint, undefined),
      staleTime: 1000 * 60 * 5, // 5 minutes
    })) ?? []

  await queryClient.prefetchQuery({
    queryKey: v3TokensMetricsQueryKey(chainId, selectionKey, t24, t48, tWeek, latestIndexedBlockNumber),
    queryFn: () =>
      fetchV3TokenMetrics(chainId, endpoint, baseTokens, undefined, t24, t48, tWeek, {
        providedBlocks: blocks,
        latestIndexedBlockNumber,
      }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

type UseV3TokenDataOptions = SharedMetricsOptions & {
  enabled?: boolean
}

export const useV3TokenDatas = (tokenIds?: string[], options?: UseV3TokenDataOptions) => {
  const defaultTimestamps = useDeltaTimestamps()
  const [t24, t48, tWeek] = options?.timestamps ?? defaultTimestamps
  const providedBlocks = options?.blocks
  const latestIndexedBlockNumber = options?.latestIndexedBlockNumber
  const enabled = (options?.enabled ?? true) && (tokenIds === undefined || tokenIds.length > 0)
  const { chainId } = useConnection()
  const subgraphConfig = useSubgraphConfig(SubgraphType.V3)
  const endpoint = subgraphConfig.endpoint
  const queryClient = useQueryClient()
  const [hasRefetchedEmptyBase, setHasRefetchedEmptyBase] = useState(false)

  const normalizedTokenIds = useMemo(() => dedupeV3TokenIds(tokenIds), [tokenIds])
  const selectionKey = useMemo(() => buildV3TokenSelectionKey(normalizedTokenIds), [normalizedTokenIds])
  const baseQueryKey = useMemo(() => v3TokensBaseQueryKey(chainId, selectionKey), [chainId, selectionKey])

  const isTopQuery = normalizedTokenIds === undefined
  const tokenIdsForQuery = normalizedTokenIds ?? []

  const topTokensQuery = useGetV3TopTokensQuery<GetV3TopTokensQuery>(
    {
      endpoint,
    },
    undefined,
    {
      enabled: enabled && isTopQuery,
      queryKey: baseQueryKey,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV3TopTokensQuery) => response,
    }
  )

  const tokensByIdsQuery = useGetV3TokensByIdsQuery<GetV3TokensByIdsQuery>(
    {
      endpoint,
    },
    {
      ids: tokenIdsForQuery,
    },
    {
      enabled: enabled && !isTopQuery && tokenIdsForQuery.length > 0,
      queryKey: baseQueryKey,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV3TokensByIdsQuery) => response,
    }
  )

  const baseQuery = isTopQuery ? topTokensQuery : tokensByIdsQuery

  const baseTokenSnapshots = useMemo(() => {
    const source = isTopQuery ? topTokensQuery.data : tokensByIdsQuery.data
    if (!source) return undefined
    const tokens = source.tokens ?? []
    const ethPrice = source.bundles?.[0]?.ethPriceUSD
    return parseTokenSnapshot(tokens, ethPrice)
  }, [isTopQuery, topTokensQuery.data, tokensByIdsQuery.data])

  const baseData = baseTokenSnapshots
  const baseDataLength = baseData?.length ?? 0
  const deferEmptyResult = (!baseData && baseQuery.isFetching) || (baseDataLength === 0 && baseQuery.isFetching)

  useEffect(() => {
    if (!enabled) {
      if (hasRefetchedEmptyBase) {
        setHasRefetchedEmptyBase(false)
      }
      return
    }

    if (!baseQuery.isFetched || baseQuery.isFetching) return

    if (baseDataLength > 0) {
      if (hasRefetchedEmptyBase) {
        setHasRefetchedEmptyBase(false)
      }
      return
    }

    if (hasRefetchedEmptyBase) return

    setHasRefetchedEmptyBase(true)
    void baseQuery.refetch()
  }, [enabled, baseDataLength, baseQuery, hasRefetchedEmptyBase])

  useEffect(() => {
    setHasRefetchedEmptyBase(false)
  }, [selectionKey])

  const metricsQueryKey = useMemo(
    () => v3TokensMetricsQueryKey(chainId, selectionKey, t24, t48, tWeek, latestIndexedBlockNumber),
    [chainId, selectionKey, t24, t48, tWeek, latestIndexedBlockNumber]
  )

  const metricsQuery = useQuery({
    queryKey: metricsQueryKey,
    queryFn: () => {
      const previousMetrics = queryClient.getQueryData<Record<string, V3TokenMetrics>>(metricsQueryKey)
      return fetchV3TokenMetrics(chainId, endpoint, baseData ?? [], normalizedTokenIds, t24, t48, tWeek, {
        providedBlocks,
        previousMetrics,
        latestIndexedBlockNumber,
      })
    },
    enabled: enabled && baseDataLength > 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  const metricsData = metricsQuery.data

  const baseTokens = useMemo(() => {
    if (deferEmptyResult) return undefined
    if (!baseDataLength) return undefined
    return (baseData || []).map((t) => createV3TokenData(t))
  }, [baseData, baseDataLength, deferEmptyResult])

  const combinedData = useMemo(() => {
    if (!baseTokens || !metricsData) return undefined
    const metricsById = metricsData

    return baseTokens.map((token) => {
      const metrics = metricsById[token.id.toLowerCase()]
      return metrics ? { ...token, ...metrics } : token
    })
  }, [baseTokens, metricsData])

  const refetch = async () => {
    const baseResult = await baseQuery.refetch({ cancelRefetch: false })
    if (!metricsQuery.isPaused) {
      await metricsQuery.refetch({ cancelRefetch: false })
    }
    return baseResult
  }

  const hasCombinedData = combinedData !== undefined

  return {
    data: combinedData,
    isLoading: (baseQuery.isLoading || metricsQuery.isLoading) && !hasCombinedData,
    isFetching: baseQuery.isFetching || metricsQuery.isFetching,
    error: baseQuery.error ?? metricsQuery.error,
    refetch,
  }
}

type UseV3TokenDatasWithChangesOptions = SharedMetricsOptions & {
  enabled?: boolean
}

export const useTokenDatasWithChanges = (options?: UseV3TokenDatasWithChangesOptions) => {
  const defaultTimestamps = useDeltaTimestamps()
  const [t24, t48, tWeek] = options?.timestamps ?? defaultTimestamps
  const providedBlocks = options?.blocks
  const latestIndexedBlockNumber = options?.latestIndexedBlockNumber
  const { chainId } = useConnection()
  const selectionKey = useMemo(() => buildV3TokenSelectionKey(undefined), [])
  const enabled = options?.enabled ?? true
  const subgraphConfig = useSubgraphConfig(SubgraphType.V3)
  const endpoint = subgraphConfig.endpoint
  const queryClient = useQueryClient()
  const [hasRefetchedEmptyBase, setHasRefetchedEmptyBase] = useState(false)

  const baseQueryKey = useMemo(() => v3TokensBaseQueryKey(chainId, selectionKey), [chainId, selectionKey])

  const baseQuery = useGetV3TopTokensQuery<GetV3TopTokensQuery>(
    {
      endpoint,
    },
    undefined,
    {
      enabled,
      queryKey: baseQueryKey,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV3TopTokensQuery) => response,
    }
  )

  const baseTokenSnapshotsAll = useMemo(() => {
    if (!baseQuery.data) return undefined
    const tokens = baseQuery.data.tokens ?? []
    const ethPrice = baseQuery.data.bundles?.[0]?.ethPriceUSD
    return parseTokenSnapshot(tokens, ethPrice)
  }, [baseQuery.data])

  const baseDataLength = baseTokenSnapshotsAll?.length ?? 0
  const deferEmptyResult =
    (!baseTokenSnapshotsAll && baseQuery.isFetching) || (baseDataLength === 0 && baseQuery.isFetching)

  const metricsQueryKey = useMemo(
    () => v3TokensMetricsQueryKey(chainId, selectionKey, t24, t48, tWeek, latestIndexedBlockNumber),
    [chainId, selectionKey, t24, t48, tWeek, latestIndexedBlockNumber]
  )

  useEffect(() => {
    if (!enabled) {
      if (hasRefetchedEmptyBase) {
        setHasRefetchedEmptyBase(false)
      }
      return
    }

    if (!baseQuery.isFetched || baseQuery.isFetching) return

    if (baseDataLength > 0) {
      if (hasRefetchedEmptyBase) {
        setHasRefetchedEmptyBase(false)
      }
      return
    }

    if (hasRefetchedEmptyBase) return

    setHasRefetchedEmptyBase(true)
    void baseQuery.refetch()
  }, [enabled, baseDataLength, baseQuery, hasRefetchedEmptyBase])

  const metricsQuery = useQuery({
    queryKey: metricsQueryKey,
    queryFn: () => {
      const previousMetrics = queryClient.getQueryData<Record<string, V3TokenMetrics>>(metricsQueryKey)
      return fetchV3TokenMetrics(chainId, endpoint, baseTokenSnapshotsAll ?? [], undefined, t24, t48, tWeek, {
        providedBlocks,
        previousMetrics,
        latestIndexedBlockNumber,
      })
    },
    enabled: enabled && baseDataLength > 0 && (options?.blocksRequired ?? true),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  const metricsData = metricsQuery.data

  const baseTokens = useMemo(() => {
    if (deferEmptyResult) return undefined
    if (!baseDataLength) return undefined
    return (baseTokenSnapshotsAll || []).map((t) => createV3TokenData(t))
  }, [baseTokenSnapshotsAll, baseDataLength, deferEmptyResult])

  const combinedData = useMemo(() => {
    if (!baseTokens || !metricsData) return undefined
    const metricsById = metricsData

    return baseTokens.map((t) => {
      const metrics = metricsById[t.id.toLowerCase()]
      return metrics ? { ...t, ...metrics } : t
    })
  }, [baseTokens, metricsData])

  const refetch = async () => {
    const baseResult = await baseQuery.refetch({ cancelRefetch: false })
    if (!metricsQuery.isPaused) {
      await metricsQuery.refetch({ cancelRefetch: false })
    }
    return baseResult
  }

  const hasCombinedData = combinedData !== undefined

  return {
    data: combinedData,
    isLoading: (baseQuery.isLoading || metricsQuery.isLoading) && !hasCombinedData,
    isFetching: baseQuery.isFetching || metricsQuery.isFetching,
    error: baseQuery.error ?? metricsQuery.error,
    refetch,
  }
}
